import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardHeader,
  CardBody,
  Heading,
  Button,
  Select,
  FormControl,
  FormLabel,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useToast,
  Badge,
  Flex,
  VStack,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useDisclosure,
  Input,
} from '@chakra-ui/react';
import { useAuth } from '../../context/AuthContext';
import { db } from '../../lib/firebase';
import { LeagueId, Match as AppMatch } from '../../types';
import {
  filterMatchesByMundialJornada,
  getMundialGroupMatches,
  isMundialGroupMatch,
  MUNDIAL_GROUP_JORNADAS,
} from '../../services/matchdayService';
import { ResponsiveTableContainer } from '../ui/ResponsiveTableContainer';
import { 
  collection, 
  doc, 
  getDocs, 
  setDoc, 
  query, 
  where, 
  writeBatch,
  Timestamp,
  DocumentData,
  QueryDocumentSnapshot,
  updateDoc,
  deleteDoc
} from 'firebase/firestore';

const LEAGUE_NAMES: Record<LeagueId, string> = {
  liga_mx: 'Liga BBVA MX',
  mundial_fifa: 'Mundial FIFA 2026',
};

interface Match {
  id: string;
  league?: LeagueId;
  phase: string;
  team_a: string;
  team_b: string | null;
  score_a: number | null;
  score_b: number | null;
  activo: boolean;
  date: Timestamp;
  created_at: Timestamp;
  updated_at: Timestamp;
  group?: string | null;
  match_type?: string;
}

interface GroupedMatches {
  [phase: string]: Match[];
}

const teams = [
  'América',
  'Guadalajara',
  'Cruz Azul',
  'Pumas UNAM',
  'Monterrey',
  'Tigres UANL',
  'Santos Laguna',
  'Atlas',
  'Toluca',
  'León',
  'Pachuca',
  'Tijuana',
  'Necaxa',
  'Puebla',
  'Querétaro',
  'Mazatlán FC',
  'Juárez',
  'Atlético San Luis'
];

const LIGA_MX_PHASES = [
  'Jornada 1', 'Jornada 2', 'Jornada 3', 'Jornada 4',
  'Jornada 5', 'Jornada 6', 'Jornada 7', 'Jornada 8',
  'Jornada 9', 'Jornada 10', 'Jornada 11', 'Jornada 12',
  'Jornada 13', 'Jornada 14', 'Jornada 15', 'Jornada 16',
  'Jornada 17',
  'Cuartos Ida', 'Cuartos Vuelta',
  'Semifinal Ida', 'Semifinal Vuelta',
  'Final Ida', 'Final Vuelta'
];

const MUNDIAL_PHASES = [
  'Fase de Grupos', 'Octavos de final', 'Cuartos de final', 'Semifinal', 'Tercer lugar', 'Final'
];

const ManageMatches: React.FC = () => {
  const [league, setLeague] = useState<LeagueId>('liga_mx');
  const [selectedPhase, setSelectedPhase] = useState('');
  const [teamA, setTeamA] = useState('');
  const [teamB, setTeamB] = useState('');
  const [allMatches, setAllMatches] = useState<Match[]>([]);
  const [matches, setMatches] = useState<GroupedMatches>({});
  const [loading, setLoading] = useState(false);
  const [phaseToDelete, setPhaseToDelete] = useState<string | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = React.useRef<HTMLButtonElement>(null);
  const toast = useToast();
  const { user } = useAuth();

  const phases = league === 'mundial_fifa' ? MUNDIAL_PHASES : LIGA_MX_PHASES;

  useEffect(() => {
    loadMatches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [league]);

  const loadMatches = async () => {
    try {
      const matchesRef = collection(db, 'matches');
      const matchesSnap = await getDocs(matchesRef);
      const matchesData = matchesSnap.docs.map((doc: QueryDocumentSnapshot<DocumentData>) => ({
        id: doc.id,
        ...doc.data()
      })) as Match[];

      const filtered = matchesData.filter(m =>
        m.league === league || (league === 'liga_mx' && !m.league)
      );

      setAllMatches(filtered);

      const groupedMatches = filtered.reduce((acc: GroupedMatches, match) => {
        if (!acc[match.phase]) {
          acc[match.phase] = [];
        }
        acc[match.phase].push(match);
        return acc;
      }, {});

      setMatches(groupedMatches);
    } catch (error) {
      console.error('Error al cargar partidos:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los partidos',
        status: 'error',
        duration: 3000,
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedPhase || !teamA || !teamB) {
      toast({
        title: 'Error',
        description: 'Por favor completa todos los campos',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    setLoading(true);
    try {
      const matchRef = doc(collection(db, 'matches'));
      await setDoc(matchRef, {
        league,
        phase: selectedPhase,
        team_a: teamA,
        team_b: teamB,
        score_a: null,
        score_b: null,
        activo: true,
        date: Timestamp.now(),
        created_at: Timestamp.now(),
        updated_at: Timestamp.now()
      });

      toast({
        title: 'Éxito',
        description: 'Partido creado correctamente',
        status: 'success',
        duration: 3000,
      });

      // Limpiar formulario
      setSelectedPhase('');
      setTeamA('');
      setTeamB('');
      
      // Recargar partidos
      loadMatches();
    } catch (error) {
      console.error('Error al crear partido:', error);
      toast({
        title: 'Error',
        description: 'No se pudo crear el partido',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const toggleMatchActive = async (matchId: string, active: boolean) => {
    try {
      await updateDoc(doc(db, 'matches', matchId), {
        activo: active,
        updated_at: Timestamp.now(),
      });
      toast({
        title: 'Éxito',
        description: `Partido ${active ? 'activado' : 'desactivado'} correctamente`,
        status: 'success',
        duration: 3000,
      });
      loadMatches();
    } catch (error) {
      console.error('Error al actualizar partido:', error);
      toast({
        title: 'Error',
        description: 'No se pudo cambiar el estado del partido',
        status: 'error',
        duration: 3000,
      });
    }
  };

  const renderMatchActions = (match: Match) => (
    <Flex gap={2} flexWrap="wrap">
      <Button
        size="sm"
        colorScheme={match.activo ? 'orange' : 'green'}
        variant={match.activo ? 'outline' : 'solid'}
        onClick={() => toggleMatchActive(match.id, !match.activo)}
      >
        {match.activo ? 'Desactivar' : 'Activar'}
      </Button>
      <Button
        colorScheme="red"
        size="sm"
        variant="outline"
        onClick={() => handleResetMatch(match.id)}
      >
        Reiniciar
      </Button>
    </Flex>
  );

  const toggleMundialJornada = async (jornada: number, active: boolean) => {
    try {
      const groupPool = getMundialGroupMatches(allMatches as unknown as AppMatch[]);
      const jornadaMatches = filterMatchesByMundialJornada(
        groupPool,
        jornada,
        groupPool
      );
      const batch = writeBatch(db);

      if (active) {
        jornadaMatches.forEach((m) => {
          batch.update(doc(db, 'matches', m.id), { activo: true });
        });
      } else {
        jornadaMatches.forEach((m) => {
          batch.update(doc(db, 'matches', m.id), { activo: false });
        });
      }

      await batch.commit();
      toast({
        title: 'Éxito',
        description: `Jornada ${jornada} ${active ? 'activada' : 'desactivada'} correctamente`,
        status: 'success',
        duration: 3000,
      });
      loadMatches();
    } catch (error) {
      console.error('Error al actualizar jornada:', error);
      toast({
        title: 'Error',
        description: 'No se pudo actualizar la jornada',
        status: 'error',
        duration: 3000,
      });
    }
  };

  const togglePhase = async (phase: string, active: boolean) => {
    try {
      const matchesRef = collection(db, 'matches');
      const q = query(matchesRef, where('phase', '==', phase));
      const querySnapshot = await getDocs(q);
      const docsToUpdate = querySnapshot.docs.filter((docSnap) => {
        const data = docSnap.data();
        const matchLeague = data.league ?? 'liga_mx';
        return matchLeague === league;
      });
      const batch = writeBatch(db);
      docsToUpdate.forEach((docSnap) => {
        batch.update(docSnap.ref, { activo: active });
      });
      await batch.commit();
      toast({
        title: 'Éxito',
        description: `Jornada ${active ? 'activada' : 'desactivada'} correctamente`,
        status: 'success',
        duration: 3000,
      });

      loadMatches();
    } catch (error) {
      console.error('Error al actualizar fase:', error);
      toast({
        title: 'Error',
        description: 'No se pudo actualizar la jornada',
        status: 'error',
        duration: 3000,
      });
    }
  };

  const handleResetMatch = async (matchId: string) => {
    try {
      // 1. Reinicia el resultado real del partido
      const matchRef = doc(db, 'matches', matchId);
      await updateDoc(matchRef, {
        score_a: null,
        score_b: null
      });

      // 2. Elimina los pronósticos de ese partido
      const predictionsRef = collection(db, 'predictions');
      const q = query(predictionsRef, where('match_id', '==', matchId));
      const predictionsSnap = await getDocs(q);
      const batchDeletes = predictionsSnap.docs.map(docSnap => deleteDoc(docSnap.ref));
      await Promise.all(batchDeletes);

      toast({
        title: 'Partido reiniciado',
        description: 'El resultado y los pronósticos han sido reiniciados.',
        status: 'success',
        duration: 3000,
      });

      // (Opcional) Recarga los partidos
      // await loadMatches();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudo reiniciar el partido.',
        status: 'error',
        duration: 3000,
      });
    }
  };

  const handleDeletePhase = async () => {
    if (!phaseToDelete) return;

    try {
      setLoading(true);

      const matchesRef = collection(db, 'matches');
      const q = query(matchesRef, where('phase', '==', phaseToDelete));
      const matchesSnap = await getDocs(q);
      const docsToDelete = matchesSnap.docs.filter((docSnap) => {
        const data = docSnap.data();
        const matchLeague = data.league ?? 'liga_mx';
        return matchLeague === league;
      });
      const matchIds = docsToDelete.map((d) => d.id);
      
      const predictionsRef = collection(db, 'predictions');
      const predictionsQuery = query(
        predictionsRef,
        where('match_id', 'in', matchIds)
      );
      const predictionsSnap = await getDocs(predictionsQuery);

      const batch = writeBatch(db);
      predictionsSnap.docs.forEach((docSnap) => {
        batch.delete(docSnap.ref);
      });
      docsToDelete.forEach((docSnap) => {
        batch.delete(docSnap.ref);
      });
      
      await batch.commit();
      
      toast({
        title: 'Jornada eliminada',
        description: `La jornada "${phaseToDelete}" y todos sus pronósticos han sido eliminados`,
        status: 'success',
        duration: 5000,
      });
      
      // Cerrar diálogo y limpiar estado
      onClose();
      setPhaseToDelete(null);
      
      // Recargar partidos
      loadMatches();
    } catch (error) {
      console.error('Error al eliminar jornada:', error);
      toast({
        title: 'Error',
        description: 'No se pudo eliminar la jornada',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  const confirmDeletePhase = (phase: string) => {
    setPhaseToDelete(phase);
    onOpen();
  };

  if (!user || user.email !== 'htorres@teiker.mx') {
    return (
      <Box p={4}>
        <Heading size="md">No tienes acceso a esta página</Heading>
      </Box>
    );
  }

  return (
    <VStack spacing={6} align="stretch" p={4}>
      <Heading>Administración de Partidos</Heading>

      <FormControl>
        <FormLabel>Liga / Torneo</FormLabel>
        <Select
          value={league}
          onChange={(e) => { setLeague(e.target.value as LeagueId); setSelectedPhase(''); setTeamA(''); setTeamB(''); }}
        >
          <option value="liga_mx">{LEAGUE_NAMES.liga_mx}</option>
          <option value="mundial_fifa">{LEAGUE_NAMES.mundial_fifa}</option>
        </Select>
      </FormControl>

      <Card>
        <CardHeader>
          <Heading size="md">Agregar Nuevo Partido - {LEAGUE_NAMES[league]}</Heading>
        </CardHeader>
        <CardBody>
          <form onSubmit={handleSubmit}>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Fase</FormLabel>
                <Select 
                  value={selectedPhase} 
                  onChange={(e) => setSelectedPhase(e.target.value)}
                  placeholder="Selecciona la fase"
                >
                  {phases.map((phase) => (
                    <option key={phase} value={phase}>{phase}</option>
                  ))}
                </Select>
              </FormControl>

              {league === 'mundial_fifa' ? (
                <>
                  <FormControl isRequired>
                    <FormLabel>Equipo Local</FormLabel>
                    <Input
                      value={teamA}
                      onChange={(e) => setTeamA(e.target.value)}
                      placeholder="Nombre del equipo local"
                    />
                  </FormControl>
                  <FormControl isRequired>
                    <FormLabel>Equipo Visitante</FormLabel>
                    <Input
                      value={teamB}
                      onChange={(e) => setTeamB(e.target.value)}
                      placeholder="Nombre del equipo visitante"
                    />
                  </FormControl>
                </>
              ) : (
                <>
                  <FormControl isRequired>
                    <FormLabel>Equipo Local</FormLabel>
                    <Select 
                      value={teamA} 
                      onChange={(e) => setTeamA(e.target.value)}
                      placeholder="Selecciona el equipo local"
                    >
                      {teams.map((team) => (
                        <option key={team} value={team}>{team}</option>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl isRequired>
                    <FormLabel>Equipo Visitante</FormLabel>
                    <Select 
                      value={teamB} 
                      onChange={(e) => setTeamB(e.target.value)}
                      placeholder="Selecciona el equipo visitante"
                    >
                      {teams.map((team) => (
                        <option key={team} value={team}>{team}</option>
                      ))}
                    </Select>
                  </FormControl>
                </>
              )}

              <Button 
                type="submit" 
                colorScheme="blue" 
                isLoading={loading}
                width="full"
              >
                Agregar Partido
              </Button>
            </VStack>
          </form>
        </CardBody>
      </Card>

      {/* Lista de partidos por fase / jornada */}
      {league === 'mundial_fifa' && (
        <>
          {MUNDIAL_GROUP_JORNADAS.map((jornada) => {
            const groupPool = getMundialGroupMatches(allMatches as unknown as AppMatch[]);
            const jornadaMatches = filterMatchesByMundialJornada(
              groupPool,
              jornada,
              groupPool
            );
            if (jornadaMatches.length === 0) return null;
            const isActive = jornadaMatches.some((m) => m.activo);
            const byGroup = jornadaMatches.reduce((acc: GroupedMatches, match) => {
              const key = match.group ? `Grupo ${match.group}` : match.phase;
              if (!acc[key]) acc[key] = [];
              acc[key].push(match as unknown as Match);
              return acc;
            }, {});

            return (
              <Card key={`jornada-${jornada}`}>
                <CardHeader>
                  <Flex justify="space-between" align="center" flexWrap="wrap" gap={2}>
                    <Heading size="md">Jornada {jornada} — Fase de grupos</Heading>
                    <Button
                      size="sm"
                      colorScheme={isActive ? 'red' : 'green'}
                      onClick={() => toggleMundialJornada(jornada, !isActive)}
                    >
                      {isActive ? 'Desactivar' : 'Activar'} Jornada
                    </Button>
                  </Flex>
                </CardHeader>
                <CardBody>
                  {Object.entries(byGroup).map(([groupLabel, groupMatches]) => (
                    <Box key={groupLabel} mb={4}>
                      <Heading size="sm" mb={2}>
                        {groupLabel}
                      </Heading>
                      <ResponsiveTableContainer>
                      <Table variant="simple" size="sm">
                        <Thead>
                          <Tr>
                            <Th>Local</Th>
                            <Th>Visitante</Th>
                            <Th>Estado</Th>
                            <Th>Acciones</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {groupMatches.map((match) => (
                            <Tr key={match.id}>
                              <Td>{match.team_a}</Td>
                              <Td>{match.team_b}</Td>
                              <Td>
                                <Badge colorScheme={match.activo ? 'green' : 'red'}>
                                  {match.activo ? 'Activo' : 'Inactivo'}
                                </Badge>
                              </Td>
                              <Td>{renderMatchActions(match)}</Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                      </ResponsiveTableContainer>
                    </Box>
                  ))}
                </CardBody>
              </Card>
            );
          })}

          {Object.entries(
            allMatches
              .filter((m) => !isMundialGroupMatch(m as unknown as AppMatch))
              .reduce((acc: GroupedMatches, match) => {
                if (!acc[match.phase]) acc[match.phase] = [];
                acc[match.phase].push(match);
                return acc;
              }, {})
          ).map(([phase, phaseMatches]) => (
            <Card key={phase}>
              <CardHeader>
                <Flex justify="space-between" align="center" flexWrap="wrap" gap={2}>
                  <Heading size="md">{phase}</Heading>
                  <Flex gap={2} flexWrap="wrap">
                    <Button
                      size="sm"
                      colorScheme={phaseMatches[0]?.activo ? 'red' : 'green'}
                      onClick={() => togglePhase(phase, !phaseMatches[0]?.activo)}
                    >
                      {phaseMatches[0]?.activo ? 'Desactivar' : 'Activar'} Fase
                    </Button>
                  </Flex>
                </Flex>
              </CardHeader>
              <CardBody>
                <ResponsiveTableContainer>
                <Table variant="simple">
                  <Thead>
                    <Tr>
                      <Th>Local</Th>
                      <Th>Visitante</Th>
                      <Th>Estado</Th>
                      <Th>Acciones</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {phaseMatches.map((match) => (
                      <Tr key={match.id}>
                        <Td>{match.team_a}</Td>
                        <Td>{match.team_b}</Td>
                        <Td>
                          <Badge colorScheme={match.activo ? 'green' : 'red'}>
                            {match.activo ? 'Activo' : 'Inactivo'}
                          </Badge>
                        </Td>
                        <Td>{renderMatchActions(match)}</Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
                </ResponsiveTableContainer>
              </CardBody>
            </Card>
          ))}
        </>
      )}

      {league === 'liga_mx' &&
        Object.entries(matches).map(([phase, phaseMatches]) => (
        <Card key={phase}>
          <CardHeader>
            <Flex justify="space-between" align="center" flexWrap="wrap" gap={2}>
              <Heading size="md">{phase}</Heading>
              <Flex gap={2} flexWrap="wrap">
                <Button
                  size="sm"
                  colorScheme={phaseMatches[0]?.activo ? "red" : "green"}
                  onClick={() => togglePhase(phase, !phaseMatches[0]?.activo)}
                >
                  {phaseMatches[0]?.activo ? "Desactivar" : "Activar"} Jornada
                </Button>
                <Button
                  size="sm"
                  colorScheme="red"
                  variant="outline"
                  onClick={() => confirmDeletePhase(phase)}
                >
                  Eliminar Jornada
                </Button>
              </Flex>
            </Flex>
          </CardHeader>
          <CardBody>
      <ResponsiveTableContainer>
      <Table variant="simple">
        <Thead>
          <Tr>
            <Th>Local</Th>
            <Th>Visitante</Th>
            <Th>Estado</Th>
            <Th>Acciones</Th>
          </Tr>
        </Thead>
        <Tbody>
                {phaseMatches.map((match) => (
                  <Tr key={match.id}>
                    <Td>{match.team_a}</Td>
                    <Td>{match.team_b}</Td>
                    <Td>
                      <Badge colorScheme={match.activo ? "green" : "red"}>
                        {match.activo ? "Activo" : "Inactivo"}
                      </Badge>
                    </Td>
                    <Td>{renderMatchActions(match)}</Td>
                  </Tr>
                ))}
        </Tbody>
      </Table>
      </ResponsiveTableContainer>
          </CardBody>
        </Card>
      ))}

      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Eliminar Jornada
            </AlertDialogHeader>

            <AlertDialogBody>
              ¿Estás seguro de que deseas eliminar la jornada "{phaseToDelete}"? 
              Esta acción eliminará todos los partidos y pronósticos asociados. 
              Esta acción no se puede deshacer.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onClose}>
                Cancelar
              </Button>
              <Button 
                colorScheme="red" 
                onClick={handleDeletePhase} 
                ml={3}
                isLoading={loading}
              >
                Eliminar
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </VStack>
  );
};

export default ManageMatches; 